/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  Card,
  DatePicker,
  Tabs,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ContainerOutlined, SaveOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales, getMeses } from 'api/catalogos/index';
import {
  CRUDEvaluacionGeneralClock,
  formatEvaluacionById,
} from 'api/configuraciones/clock/EvaluacionClock/EvaluacionGeneral';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import EvaluacionMensualReloj from './EvaluacionMensual';
import EvaluacionEmpleado from './EvaluacionEmpleado';

const evaluacionRelojPermName = 'evaluacioncierre';
const nameModelSingular = 'Evaluación Mensual';

export const evaluacionRelojPermission = {
  permissionModel: evaluacionRelojPermName,
  catalogsModels: [],
  tabsModels: [],
};

const EvaluacionGeneralReloj = ({ permission }) => {
  const [form] = Form.useForm();
  const [formChild] = Form.useForm();
  const [errores, setErrores] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [pageActual, setPageActual] = useState(1);
  const [open, setVisible] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);

  const [visibleErrores, setVisibleErrores] = useState(false);
  const onErrors = () => {
    setVisibleErrores(true);
  };

  const registroName = selectedData?.mes?.nombre;

  const cliente = CRUDEvaluacionGeneralClock;
  const formInstances = [
    form,
  ];

  const fetchPaginated = async (page = 1, q = null) => {
    try {
      setLoading(true);
      const cleanPage = Number(String(page).replace(/\D/g, ''));
      const _data = await cliente.obtenerListaDeRegistros(
        {
          periodo_fiscal: form.getFieldValue('periodo_fiscal'),
          page: cleanPage,
          ...q,
        },
      );
      const { results } = _data;

      setSelectedRowKeys([]);
      setPageActual(cleanPage);
      setData({ ..._data, results: [...results] });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await cliente.obtenerListaDeRegistros({ periodo_fiscal: form.getFieldValue('periodo_fiscal') });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _periodosFiscales,
      ] = await Promise.all([
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
      ]);
      if (mounted) {
        const defaultYear = _periodosFiscales[_periodosFiscales.length - 1];
        form.setFieldValue('periodo_fiscal', defaultYear?.id);
        setPeriodosFiscales(_periodosFiscales);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onClickBuscar = async () => {
    try {
      await form.validateFields();
      setLoading(true);
      await fetchData();
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Nivel de Estudios ya autorizado');
        return;
      }
      const saved = await await cliente.autorizarRegistro(key);
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await formChild.validateFields();
      const values = formChild.getFieldsValue();
      const [key] = selectedRowKeys;

      const saved = await (
        key
          ? cliente.actualizarRegistro(key, values)
          : cliente.crearRegistro(values)
      );
      if (saved) {
        await fetchData(pageActual);
        handleOnRowClick(saved);
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };
  const onClickAdd = async () => {
    try {
      setLoading(true);
      await formChild.validateFields();
      const values = formatEvaluacionById({
        periodo_fiscal: form.getFieldValue('periodo_fiscal'),
      });
      formChild.resetFields();
      formChild.setFieldsValue(values);
      onCancel();
      setSelectedData({
        periodo_fiscal: form.getFieldValue('periodo_fiscal'),
      });
      setVisible(true);
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const onClickEdit = async () => {
    try {
      setLoading(true);
      await formChild.validateFields();
      formChild.resetFields();
      formChild.setFieldsValue(formatEvaluacionById(selectedData));
      setVisible(true);
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Mes',
      dataIndex: 'mes',
      key: 'mes',
      width: 100,
      render: (_, record) => record?.mes?.nombre,
    },
    {
      titleText: 'Evaluacion Realizada',
      dataIndex: 'evaluacion_terminada',
      key: 'evaluacion_terminada',
      width: 80,
      render: (val) => (val ? 'Si' : 'No'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estado',
      key: 'estado',
      width: 80,
      render: (_, record) => record?.estados_globales?.descripcion,
    },
  ];

  const dataNormalizer = (dataArray) => {
    if (!Array.isArray(dataArray)) return [];
    return dataArray.map((row) => ({
      ...row,
      order: row.mes?.id,
    })).sort((a, b) => a.order - b.order);
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <>
            <Form
              layout="vertical"
              form={form}
              scrollToFirstError
              name="general"
              initialValues={{
                estados_globales: ESTATUS_ACTIVO,
              }}
            >
              <Row gutter={10}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="periodo_fiscal"
                    label="Año"
                    rules={rules.estatus}
                    hasFeedback
                  >
                    <Select
                      dataSource={periodosFiscales}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item>
                    <Button
                      onClick={() => onClickBuscar()}
                      style={{ marginTop: 20 }}
                    >
                      Consultar
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
            <Table
              cols={columns}
              data={dataNormalizer(data?.results) || []}
              pagination={false}
              paginationCallback={fetchPaginated}
              count={data?.count || 0}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              currentPage={pageActual}
              controls={{
                onClickAdd,
                onClickEdit: () => onClickEdit(),
              }}
              customActions={[
                {
                  onClick: () => setVisibleAutorizar(true),
                  permission: { model: 'evaluacioncierre', action: 'autorizar' },
                  icon: <CheckCircleOutlined />,
                  text: 'Autorizar',
                  disabledBySelection: true,
                  disabled: selectedData?.estados_globales?.id !== ESTATUS_ACTIVO,
                  className: 'success',
                },
              ]}
              permission={permission}
              allowExportParam={false}
            />
          </>
        )
          : (
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? 'EDITAR' : 'AGREGAR'} ${nameModelSingular} `}
              extra={(
                <FormSubmitControls
                  onFinish={onFinish}
                  onErrors={errores?.results?.length ? onErrors : undefined}
                  onCancel={onCancel}
                  loading={loading}
                  onFinishText="Guardar y Continuar"
                />
              )}
              bodyStyle={{ overflow: 'visible' }}
              bordered={false}
            >
              <Tabs defaultActiveKey="mensual">
                <Tabs.TabPane tab="Evaluación Mensual" key="mensual">
                  <EvaluacionMensualReloj
                    permission={permission}
                    recordFather={selectedData}
                    form={formChild}
                    errorsVisibleEffect={[visibleErrores, setVisibleErrores]}
                    errorsEffect={[errores, setErrores]}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Evaluación por Empleado" key="empleado">
                  <EvaluacionEmpleado
                    permission={permission}
                    recordFather={selectedData}
                  />
                </Tabs.TabPane>
              </Tabs>

            </Card>
          )}
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar ${nameModelSingular} ${registroName}`}
          open={visibleAutorizar}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default EvaluacionGeneralReloj;
